import type {
  EntityBody,
  TransitionRecording,
} from "@cloudnest/kinetic-core-engine";
import { useCallback, useMemo, useRef } from "react";
import type { PieceTypeInitConfig } from "./config/types";
import { useCanvasRenderer } from "./hooks/use-canvas-renderer";
import { useFlickInput } from "./hooks/use-flick-input";
import { canvasToBoard, computeLayout } from "./utils/coordinate-transform";

interface GameCanvasProps {
  readonly entities: ReadonlyArray<EntityBody>;
  readonly selectedEntityId: string | null;
  readonly isAnimating: boolean;
  readonly animationRecording: TransitionRecording | null;
  readonly currentParticipant: string;
  readonly isRunning: boolean;
  readonly canvasWidth: number;
  readonly canvasHeight: number;
  readonly canvasPadding: number;
  readonly boardCols: number;
  readonly boardRows: number;
  readonly ownerOneId: string;
  readonly pieceTypesById: ReadonlyMap<string, PieceTypeInitConfig>;
  readonly onSelectEntity: (entityId: string | null) => void;
  readonly onFlick: (
    entityId: string,
    impulseX: number,
    impulseY: number,
  ) => void;
  readonly onAnimationComplete: () => void;
}

export function GameCanvas({
  entities,
  selectedEntityId,
  isAnimating,
  animationRecording,
  currentParticipant,
  isRunning,
  canvasWidth,
  canvasHeight,
  canvasPadding,
  boardCols,
  boardRows,
  ownerOneId,
  pieceTypesById,
  onSelectEntity,
  onFlick,
  onAnimationComplete,
}: GameCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const layout = useMemo(
    () =>
      computeLayout(
        canvasWidth,
        canvasHeight,
        boardCols,
        boardRows,
        canvasPadding,
      ),
    [canvasWidth, canvasHeight, boardCols, boardRows, canvasPadding],
  );

  const selectedEntity = selectedEntityId
    ? (entities.find((entity) => entity.id === selectedEntityId) ?? null)
    : null;

  const handleFlickResult = useCallback(
    (result: { impulseX: number; impulseY: number }) => {
      if (selectedEntityId && !isAnimating && isRunning) {
        onFlick(selectedEntityId, result.impulseX, result.impulseY);
      }
    },
    [selectedEntityId, isAnimating, isRunning, onFlick],
  );

  const { dragState, handlePointerDown, handlePointerMove, handlePointerUp } =
    useFlickInput(layout, handleFlickResult);

  const currentFlickPreview = useMemo(() => {
    if (!dragState?.isDragging || !selectedEntity) return null;
    const dx = dragState.startBoardX - dragState.currentBoardX;
    const dy = dragState.startBoardY - dragState.currentBoardY;
    return {
      fromX: selectedEntity.x,
      fromY: selectedEntity.y,
      toX: selectedEntity.x + dx,
      toY: selectedEntity.y + dy,
    };
  }, [dragState, selectedEntity]);

  useCanvasRenderer(
    canvasRef,
    layout,
    entities,
    selectedEntityId,
    boardCols,
    boardRows,
    ownerOneId,
    pieceTypesById,
    currentFlickPreview,
    animationRecording,
    onAnimationComplete,
  );

  const handleCanvasPointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (isAnimating || !isRunning) return;

      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const scaleX = canvasWidth / rect.width;
      const scaleY = canvasHeight / rect.height;
      const canvasX = (e.clientX - rect.left) * scaleX;
      const canvasY = (e.clientY - rect.top) * scaleY;
      const board = canvasToBoard(canvasX, canvasY, layout);

      const clickedEntity = findEntityAtBoard(
        entities,
        board.x,
        board.y,
        currentParticipant,
      );

      if (clickedEntity) {
        if (selectedEntityId === clickedEntity.id) {
          handlePointerDown(canvasX, canvasY);
        } else {
          onSelectEntity(clickedEntity.id);
        }
      } else if (selectedEntityId) {
        handlePointerDown(canvasX, canvasY);
      } else {
        onSelectEntity(null);
      }
    },
    [
      isAnimating,
      isRunning,
      canvasWidth,
      canvasHeight,
      layout,
      entities,
      currentParticipant,
      selectedEntityId,
      onSelectEntity,
      handlePointerDown,
    ],
  );

  const handleCanvasPointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;

      const scaleX = canvasWidth / rect.width;
      const scaleY = canvasHeight / rect.height;
      handlePointerMove(
        (e.clientX - rect.left) * scaleX,
        (e.clientY - rect.top) * scaleY,
      );
    },
    [canvasWidth, canvasHeight, handlePointerMove],
  );

  const handleCanvasPointerUp = useCallback(() => {
    handlePointerUp();
  }, [handlePointerUp]);

  return (
    <canvas
      data-testid="game-canvas"
      ref={canvasRef}
      width={canvasWidth}
      height={canvasHeight}
      style={{
        maxWidth: "100%",
        height: "auto",
        borderRadius: "8px",
        boxShadow: "0 2px 8px var(--cn-alkagi-stone-shadow)",
        cursor: isAnimating
          ? "wait"
          : selectedEntityId
            ? "crosshair"
            : "pointer",
        touchAction: "none",
      }}
      onPointerDown={handleCanvasPointerDown}
      onPointerMove={handleCanvasPointerMove}
      onPointerUp={handleCanvasPointerUp}
      onPointerLeave={handleCanvasPointerUp}
    />
  );
}

function findEntityAtBoard(
  entities: ReadonlyArray<EntityBody>,
  boardX: number,
  boardY: number,
  currentParticipant: string,
): EntityBody | null {
  let closest: EntityBody | null = null;
  let closestDistance = Number.POSITIVE_INFINITY;

  for (const entity of entities) {
    if (entity.eliminated || entity.owner !== currentParticipant) continue;

    const dx = entity.x - boardX;
    const dy = entity.y - boardY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    if (distance <= entity.radius && distance < closestDistance) {
      closest = entity;
      closestDistance = distance;
    }
  }

  return closest;
}
